import React, { useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { Sheet } from './Sheet';
import { useIsMobile } from '../../hooks/useMediaQuery';

/**
 * OverflowMenu — wraps a row of action Buttons. On desktop, renders every
 * child unchanged (no visual difference from before). Below --bp-split,
 * keeps `primaryCount` children visible and folds the rest behind a "⋯"
 * trigger that opens a Sheet, so action rows never overflow narrow screens.
 * `primaryPosition` picks which end of the row stays visible ('end' keeps
 * the last child(ren) — usually the main CTA rendered last; 'start' keeps
 * the first).
 */
export function OverflowMenu({ children, primaryCount = 1, primaryPosition = 'end', title = 'Weitere Aktionen' }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const items = React.Children.toArray(children).filter(Boolean);

  if (!isMobile || items.length <= primaryCount) {
    return <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{items}</div>;
  }

  const visible = primaryPosition === 'start' ? items.slice(0, primaryCount) : items.slice(items.length - primaryCount);
  const hidden = primaryPosition === 'start' ? items.slice(primaryCount) : items.slice(0, items.length - primaryCount);

  const trigger = (
    <button
      key="overflow-trigger"
      type="button" aria-label={title} onClick={() => setOpen(true)}
      style={{
        display: 'inline-flex', width: 44, height: 44, alignItems: 'center', justifyContent: 'center',
        border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)',
        background: 'var(--white)', color: 'var(--text-strong)', cursor: 'pointer', flexShrink: 0,
      }}
    >
      <MoreVertical width={18} height={18} />
    </button>
  );

  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
      {primaryPosition === 'end' && trigger}
      {visible}
      {primaryPosition === 'start' && trigger}

      <Sheet open={open} onClose={() => setOpen(false)} title={title}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {hidden.map((child, i) => (
            <div key={i} onClick={() => setOpen(false)}>
              {React.cloneElement(child, { fullWidth: true })}
            </div>
          ))}
        </div>
      </Sheet>
    </div>
  );
}
